import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CardActions,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Link, useParams } from "react-router-dom";
import { API_ENDPOINT } from "../../utils/constants";
import MuPb from "../../widgets/MuPb";
import OtpInput from "./OtpInput";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { toastError } from "../../utils/util";
import {
  deleteOnsite,
  deleteOtpVerify,
  getOtpVerify,
  isOnsiteActivated,
  saveOnsite,
  saveOtpVerify,
} from "./utils";
import OnsiteReuseContainer from "./OnsiteReuseContainer";
import { Link2Off, ShieldAlert, TriangleAlert } from "lucide-react";

const SharableOnsite = () => {
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState("");
  const [currentGroup, setCurrentGroup] = useState();
  const [requesting, setRequesting] = useState(false);

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_ENDPOINT}/onsite/get-modules`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${id}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("LINK_EXPIRED");
          }

          throw new Error("LOAD_FAILED");
        }

        const data = await response.json();

        setModules(data);
      } catch (err) {
        setError(err.message || "LOAD_FAILED");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchModules();
    }
  }, [id]);

  if (loading) {
    return <MuPb />;
  }

  if (error) {
    const isExpired = error === "LINK_EXPIRED";

    return (
      <OnsiteReuseContainer maxWidth="sm">
        <Box
          sx={{
            minHeight: "70vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 540,
              textAlign: "center",
              backgroundColor: "background.paper",
              borderRadius: 4,
              px: {
                xs: 3,
                sm: 5,
              },
              py: {
                xs: 4,
                sm: 5,
              },
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 14px 45px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                width: 82,
                height: 82,
                borderRadius: "50%",
                backgroundColor: isExpired ? "#fff1f1" : "#fff7e6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              {isExpired ? (
                <Link2Off size={40} strokeWidth={1.8} color="#d32f2f" />
              ) : (
                <TriangleAlert size={40} strokeWidth={1.8} color="#ed6c02" />
              )}
            </Box>

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1.5,
                fontSize: {
                  xs: "1.7rem",
                  sm: "2rem",
                },
              }}
            >
              {isExpired ? "Access Link Expired" : "Unable to Load Modules"}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 430,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: "1rem",
              }}
            >
              {isExpired
                ? "This onsite access link is no longer valid or has expired."
                : "We couldn't load the onsite modules at the moment. Please try again later."}
            </Typography>

            {isExpired && (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1.5,
                    maxWidth: 430,
                    mx: "auto",
                    lineHeight: 1.7,
                    fontWeight: 600,
                  }}
                >
                  Please contact the IMTMA Tech Admin/Team to request a new
                  access link.
                </Typography>

                <Box
                  sx={{
                    mt: 4,
                    px: 2,
                    py: 1.4,
                    borderRadius: 2.5,
                    backgroundColor: "action.hover",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    maxWidth: "100%",
                  }}
                >
                  <ShieldAlert size={18} strokeWidth={1.8} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: "left",
                    }}
                  >
                    Expired access links cannot be reused.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </OnsiteReuseContainer>
    );
  }

  const handleClose = () => {
    setOpen(false);
  };

  const onOtpEntered = async (otp) => {
    setOtp(otp);
  };

  const deactivateModule = (group) => {
    deleteOnsite(group.key);
    document.location.reload();
  };

  const requestOtp = async (cd) => {
    try {
      setRequesting(true);
      setCurrentGroup(cd);

      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/request-activation-code?id=${cd._id}`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${id}`,
          },
        }
      );

      toast.success(data.message);

      saveOtpVerify(data.token);

      onActivate(cd);
      setOpen(true);
    } catch (error) {
      toastError(error);
    } finally {
      setRequesting(false);
    }
  };

  const onActivate = (cd) => {
    setCurrentGroup(cd);
    setOpen(true);
  };

  const onSubmitOtp = async () => {
    try {
      setVerifying(true);

      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/verify-activation-code?otp=${otp}&code=${getOtpVerify()}`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${id}`,
          },
        }
      );

      toast.success(data.message);

      saveOnsite(data.key, data.token);

      setOpen(false);

      deleteOtpVerify();

      setTimeout(() => {
        document.location.reload();
      }, 300);
    } catch (error) {
      toastError(error);
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyLink = (link) => {
    const fullLink = `${window.location.origin}${link}`;

    navigator.clipboard.writeText(fullLink);

    toast.success("Module link copied");
  };

  return (
    <OnsiteReuseContainer maxWidth="lg">
      <Grid container spacing={4}>
        {(modules || []).map((group, index) => (
          <Grid item xs={12} sm={6} md={4} key={group._id || index}>
            <Card
              elevation={6}
              sx={{
                borderRadius: "12px",
                transition: "0.3s",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                borderLeft: `6px solid ${group.color}`,
                padding: "16px",

                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {group.title}
                  </Typography>
                </Box>

                <Typography variant="body1" color="textSecondary" mb={2}>
                  {group.description}
                </Typography>

                {isOnsiteActivated(group.key) && (
                  <Box
                    display="flex"
                    alignItems="center"
                    bgcolor="success.light"
                    borderRadius="8px"
                    p={1}
                    mt={2}
                  >
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />

                    <Typography
                      variant="body2"
                      color="white"
                      fontWeight="medium"
                    >
                      This module is activated
                    </Typography>
                  </Box>
                )}
              </CardContent>

              <CardActions
                sx={{
                  justifyContent: "flex-end",
                  paddingRight: "16px",
                  paddingBottom: "16px",
                }}
              >
                {!isOnsiteActivated(group.key) ? (
                  <LoadingButton
                    size="small"
                    loading={currentGroup?._id === group._id && requesting}
                    variant="contained"
                    color="primary"
                    endIcon={<InfoIcon />}
                    onClick={() => requestOtp(group)}
                  >
                    Activate
                  </LoadingButton>
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Link
                      to={group.link}
                      style={{
                        textDecoration: "none",
                      }}
                    >
                      <Button size="small" variant="outlined">
                        Open
                      </Button>
                    </Link>

                    <IconButton
                      size="small"
                      onClick={() => handleCopyLink(group.link)}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>

                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<HighlightOffIcon />}
                      onClick={() => deactivateModule(group)}
                      sx={{
                        borderColor: "error.main",
                        color: "error.main",

                        "&:hover": {
                          borderColor: "error.dark",
                          backgroundColor: "error.light",
                        },
                      }}
                    >
                      Deactivate
                    </Button>
                  </Stack>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Enter the OTP to activate {currentGroup?.title}
        </DialogTitle>

        <DialogContent>
          <OtpInput onComplete={onOtpEntered} />

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mt: 2,
            }}
          >
            OTP is sent to Admin. Please contact Admin for the OTP.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>

          <LoadingButton
            loading={verifying}
            onClick={onSubmitOtp}
            color="primary"
            variant="contained"
            disabled={!otp}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </OnsiteReuseContainer>
  );
};

export default SharableOnsite;
